import { IProduct, IMenuProps } from '@orderingstack/ordering-types';
import { ICategory } from './types';
import React, { useCallback, useEffect, useState } from 'react';
import Chip from './components/Chip';
import debounce from 'lodash/debounce';
import DefaultCategorySection from './components/default/CategorySection';
import smoothscroll from 'smoothscroll-polyfill';
// @ts-ignore
import { useAvailability } from '@orderingstack/front-hooks';
import { useTransition, animated } from 'react-spring';
import DefaultBottomNav from './components/default/BottomNav';
import Categories from './components/default/Categories';
import DefaultTopNav from './components/default/TopNav';
import fallback from './icons/fallback_img.png';
let scrollTimer: NodeJS.Timeout | null = null;

smoothscroll.polyfill();

export function Menu({
  menu: originalMenu,
  handleSelectProduct,
  fallbackImg = fallback,
  lazyLoadImg,
  currency,
  timeZone,
  availabilityMarkOnly,
  availabilityCheckInterval,
  cols,
  colsLg,
  transitionProps,
  stacking = 'vertical',
  onMenuCategoryViewed,
  goToCheckout,
  goToStart,
  order,
  changeQuantity,
  components,
}: IMenuProps) {
  const { CategorySection, Header, TopNav, BottomNav, Logo, MenuTile } =
    components;
  const Category = CategorySection || DefaultCategorySection;

  let selectedId = '';
  const menu = useAvailability(
    originalMenu,
    availabilityMarkOnly,
    timeZone,
    availabilityCheckInterval
  );

  const [direction, setDirection] = useState<'after' | 'before'>('after');

  const categories = menu?.filter((x: IProduct) => x.items && x.items.length > 0)?.map?.((category: IProduct) => ({
    name: category.details?.literals?.name || '',
    id: category.id,
    img: category.details?.media?.[0]?.url,
  }));

  const [selectedCategory, setSelectedCategory] = useState<
    ICategory | undefined
  >(categories?.[0]);
  function setCategoryWithChipScroll(cat: ICategory) {
    console.log('Set', cat);
    setSelectedCategory(cat);
    const elem = document.getElementById(`chip-${cat.id}`);
    const horScroll = document.getElementById('chip-scroll');

    if (
      horScroll &&
      elem &&
      window.innerWidth / 2 - Math.abs(elem.offsetLeft - horScroll.scrollLeft) <
        0
    ) {
      horScroll.scrollLeft = elem.offsetLeft - window.innerWidth / 2;
    }
  }

  function setCategoryWithScroll(ICategory: ICategory) {
    const currentIndex =
      categories?.findIndex((item) => item.id === selectedCategory?.id) || 0;
    const nextIndex =
      categories?.findIndex((item) => item.id === ICategory.id) || 1;

    setDirection(nextIndex > currentIndex ? 'after' : 'before');
    
    setSelectedCategory(ICategory);
    const elem = document.getElementById(`ICategory-${ICategory.id}`);
    elem?.scrollIntoView({
      block: ICategory.id === categories?.[0].id ? 'center' : 'start',
      behavior: 'smooth',
    });
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedScroll = useCallback(
    debounce(setCategoryWithChipScroll, 150),
    []
  );

  function onScroll() {
    if (scrollTimer != null) {
      clearTimeout(scrollTimer);
    }
    scrollTimer = setTimeout(() => {
      console.log('scroller');
      categories?.some((cat: ICategory) => {
        const elem = document.getElementById(`ICategory-${cat.id}`);
        const top = elem ? Math.round(elem?.getBoundingClientRect().top) : null;
        const bottom = elem
          ? Math.round(elem?.getBoundingClientRect().bottom)
          : null;
        console.log(cat.id, 'top', top, 'bottom', bottom);
        if (cat.id !== selectedId && top && bottom && top <= 0 && bottom > 0) {
          selectedId = cat.id;
          debouncedScroll(cat);
          // setCategoryWithChipScroll(cat);
          return true;
        }
        return false;
      });
    }, 50);
  }

  const scrolled = document.getElementById('menu-scroll');
  useEffect(() => {
    scrolled?.addEventListener('scroll', onScroll);
    return () => {
      scrolled?.removeEventListener('scroll', onScroll);
    };
  }, [scrolled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cat = menu?.find?.((item) => item.id === selectedCategory?.id);
    if (cat) {
      onMenuCategoryViewed?.(cat);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  useEffect(() => {
    if (!selectedCategory && !!categories?.[0]) {
      setSelectedCategory(categories[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  const transitions = useTransition(
    selectedCategory?.id,
    transitionProps || {
      // exitBeforeEnter: true,
      from: {
        // opacity: 0,
        transform: `translate3d(${direction === 'after' ? '' : '-'}100%,0,0)`,
      },
      enter: {
        // opacity: 1,
        transform: 'translate3d(0%,0,0)',
      },
      leave: {
        // opacity: 0,
        transform: `translate3d(${direction === 'after' ? '-' : '+'}100%,0,0)`,
      },
      config: {
        friction: 20,
      },
    }
  );

  return (
    <div className="flex overflow-hidden relative flex-col justify-center items-center w-full h-full text-primary bg-bgColor select-none safe-area-p">
      <div className="flex flex-col w-full h-full bg-bgColor">
        <div className="block z-10 w-full bg-bgColor lg:invisible lg:h-0">
            <>
            {Header}
            <div
              className="flex overflow-x-scroll flex-row justify-between py-3 px-4 w-full text-sm shadow-md scroll-smooth lg:invisible lg:h-0"
              id="chip-scroll"
            >
              {categories &&
                categories.map((category: ICategory, index: number) => {
                  const { name, id } = category;
                  const isSelected = id === selectedCategory?.id;
                  return (
                    <Chip
                      id={`chip-${category.id}`}
                      key={name || index}
                      value={name || ''}
                      className={isSelected ? '!bg-primary-light' : ''}
                      handleOnClick={() => {
                        setCategoryWithScroll(category);
                      }}
                    />
                  );
                })}
            </div>
          </>
        </div>
        <div className="flex overflow-hidden flex-row grow pb-5 w-full bg-bgColor">
          <div className="flex overflow-hidden invisible z-10 flex-col shrink-0 p-0 w-0 bg-bgColor border-r-2 border-borderColor lg:visible lg:!w-[30vw]">
            <>
              {Logo}
              <Categories
                categories={categories}
                selectedCategoryId={selectedCategory?.id}
                onSelect={(category) => setCategoryWithScroll(category)}
                fallbackImg={fallbackImg}
              />
            </>
          </div>
          {stacking === 'vertical' && (
            <div
              className="overflow-y-scroll pb-4 bg-gray-200 scroll-smooth lg:ml-4"
              id="menu-scroll"
            >
              {menu?.map?.((ICategory: IProduct) => {
                return (
                  <Category
                    key={ICategory.id}
                    category={ICategory}
                    onProductClicked={handleSelectProduct}
                    fallbackImg={fallbackImg}
                    lazyLoadImg={lazyLoadImg}
                    currency={currency}
                    colsLg={colsLg}
                    cols={cols}
                    MenuTile={MenuTile}
                    setCategoryWithScroll={setCategoryWithScroll}
                    selectedCategory={selectedCategory}
                    menu={menu}
                  />
                );
              })}
            </div>
          )}
          {stacking === 'horizontal' && (
            <div className="flex flex-col grow-0 shrink-0 w-[70vw] h-full">
              {TopNav && (
                <TopNav
                  goToCheckout={goToCheckout}
                  goToStart={goToStart}
                  order={order}
                />
              )}
              {!TopNav && (
                <DefaultTopNav
                  goToCheckout={goToCheckout}
                  goToStart={goToStart}
                  order={order}
                />
              )}
              <div className="overflow-hidden relative grow w-full">
                {transitions((style, id) => {
                  const ICategory = menu?.find?.((cat) => cat.id === id);
                  if (!ICategory) {
                    return null;
                  }
                  return (
                    <animated.div
                      className="overflow-hidden absolute w-full h-full"
                      style={style}
                    >
                      <Category
                        category={ICategory}
                        onProductClicked={handleSelectProduct}
                        lazyLoadImg={lazyLoadImg}
                        fallbackImg={fallbackImg}
                        currency={currency}
                        colsLg={colsLg}
                        cols={cols}
                        MenuTile={MenuTile}
                        setCategoryWithScroll={setCategoryWithScroll}
                        selectedCategory={selectedCategory}
                        menu={menu}
                      />
                    </animated.div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        {BottomNav && (
          <BottomNav
            order={order}
            goToCheckout={goToCheckout}
            currency={currency}
            fallbackImg={fallbackImg}
            changeQuantity={changeQuantity}
          />
        )}
        {!BottomNav && (
          <DefaultBottomNav
            order={order}
            goToCheckout={goToCheckout}
            currency={currency}
            fallbackImg={fallbackImg}
            changeQuantity={changeQuantity}
          />
        )}
      </div>
    </div>
  );
}
